"use strict";

import axios from "axios";
import FormatString from "../helpers/formatString";
import GlobalError from "../helpers/globalError";
import Environment from "../helpers/Environment";

export class Map {
	constructor() {
		this.selectedLocale = document.querySelector('#dropdownlanguageMenu');
		this.mapType = document.querySelector('.btn-map-type[checked]');
		this.selectCountry = document.querySelector('#selectMapCountry');
		this.selectIsp = document.querySelector('#selectMapIsp');
		this.inputMapSearch = document.querySelector('#inputMapSearch');
		this.mapConfig = null;
		this.mapVersion = parseInt(document.querySelector('#map').dataset.mapVersion);
		this.zoom = 2;
		this.latitude = 20;
		this.longitude = 0;
		this.mapRef = null;
		this.selectedLayer = null;
		this.selectedBackgroundLayer = 'arcGis';
		this.tilesMap = null;
		this.geoInfo = '-';
		this.isFullscreen = false;
	}
	
	/**
	 * get map reference
	 * @return mapRef - Object - map reference
	 */
	getMap() {
		return this.mapRef;
	}
	
	/**
	 * populate list on country select element
	 * @param dataList - Object - Country list
	 */
	addCountryOption(dataList) {
		const controlSelectCountry = this.selectCountry.tomselect;
		const userCountry = window.location.pathname.split('/')[3].toLowerCase();
		controlSelectCountry.addOptions(dataList);
		
		controlSelectCountry.setValue(userCountry);
		this.toggleOverlay();
	}
	
	/**
	 * populate list on isp select element
	 * @param dataList - Object - ISP list
	 */
	addIspOption(dataList) {
		const controlSelectIsp = this.selectIsp.tomselect;
		const ispParamUrl = window.location.pathname.split('/')[5];
		const ispIdParamUrl = ispParamUrl.split('.')[0];
		controlSelectIsp.clear();
		controlSelectIsp.clearOptions();
		controlSelectIsp.addOptions(dataList);
		controlSelectIsp.setValue(ispIdParamUrl);
		
	}
	
	/**
	 * get country list from API
	 */
	getCountryList() {
		axios.post(`/${ this.selectedLocale.dataset.languageSelected }/map/get-country-list`, {
				params: {
					mapType: this.mapType.dataset.mapType,
					locale: this.selectedLocale.dataset.languageSelected
				}
			})
			.then((response) => {
				this.addCountryOption(response.data.data.ispAvailable);
			})
			.catch(function (error) {
				if (error.response && error.response.data.errors) {
					GlobalError.openErrorModal(error.response.data.errors.message, 'globalErrorMap');
				}
			});
	}
	
	/**
	 * get isp list from API
	 */
	getIspList() {
		const controlSelectIsp = this.selectIsp.tomselect;
		const controlSelectCountry = this.selectCountry.tomselect;
		controlSelectIsp.isDisabled = false;
		controlSelectIsp.sync();
		
		axios.post(`/${ this.selectedLocale.dataset.languageSelected }/map/get-isp-list-by-country`, {
				countryCode: controlSelectCountry.getValue()
			})
			.then((response) => {
				this.addIspOption(response.data.data.isp);
				this.updateUrl();
				this.updateHeaderComponent();
				this.updateDescriptionComponent();
			})
			.catch(function (error) {
				if (error.response && error.response.data.errors) {
					GlobalError.openErrorModal(error.response.data.errors.message, 'globalErrorMap');
				}
			});
	}
	
	/**
	 * Format isp name to replace space by '-'
	 * @param isp - string
	 * @return string
	 */
	formatIspName(isp) {
		return isp.replace(/[^a-z\d\s-]/gi, '').replace(/\s+/g, '-');
	}
	
	getUrl() {
		const currentUrl = window.location.pathname.split('/');
		return {
			'lang': currentUrl[1],
			'pageName': currentUrl[2],
			'country': currentUrl[3],
			'geoInfo': currentUrl[4],
			'ispInfo': currentUrl[5],
			'mapType': currentUrl[6],
		}
	}
	
	/**
	 * update current url (/[:language]/map/[:country]/geoInfo/[:ispId+IspName]/[:mapType]/?ll=[:latitude]&lg=[:longitude]&zoom=[:zoom])
	 * @param isSearchEnable - boolean - identify if search is active
	 * @param updateGeoInformations - boolean - identify if geo info should be updated
	 */
	updateUrl(isSearchEnable = false, updateGeoInformations = true) {
		const searchParams = new URLSearchParams(window.location.search);
		const locale = this.selectedLocale.dataset.languageSelected;
		const country = this.selectCountry.tomselect ? this.selectCountry.tomselect.getValue() : null;
		const selectedIsp = this.selectIsp.tomselect ? this.selectIsp.tomselect.getValue() : null;
		const ispName = selectedIsp ? this.formatIspName(this.selectIsp.tomselect.getItem(selectedIsp).dataset.ispName) : null;
		const isp = selectedIsp ? `${ this.selectIsp.tomselect.getValue() }.${ ispName }` : null;
		const mapType = this.mapType.dataset.mapType;
		let zoom = searchParams.get('zoom');
		let latitude = searchParams.get('ll');
		let longitude = searchParams.get('lg');
		
		if (country !== window.location.pathname.split('/')[3].toLowerCase()) {
			this.geoInfo = '-';
		}
		
		if (country && !isSearchEnable && this.geoInfo === '-' && updateGeoInformations && !selectedIsp) {
			const minLatitudeSelectCountry = this.selectCountry.tomselect.getItem(country).dataset.minlat;
			const minLongitudeSelectCountry = this.selectCountry.tomselect.getItem(country).dataset.minlng;
			const maxLatitudeSelectCountry = this.selectCountry.tomselect.getItem(country).dataset.maxlat;
			const maxLongitudeSelectCountry = this.selectCountry.tomselect.getItem(country).dataset.maxlng;
			this.mapRef.fitBounds([[minLatitudeSelectCountry, minLongitudeSelectCountry], [maxLatitudeSelectCountry, maxLongitudeSelectCountry]]);
		}
		
		if (updateGeoInformations) {
			const geolocationInformations = this.getGeolocationInformations();
			zoom = geolocationInformations.zoom;
			latitude = geolocationInformations.latitude;
			longitude = geolocationInformations.longitude;
		}
		
		const url = `/${ locale }/map/${ country ? country.toUpperCase() : 'ww' }/${ this.geoInfo }/${ isp ? isp : '-' }/${ mapType }?ll=${ latitude }&lg=${ longitude }&zoom=${ zoom }`;
		history.replaceState({nPerf: 'map'}, '', url);
		this.resetTileLayer('signal').then();
		
		if (selectedIsp) {
			this.getLastUpdate();
		}
	}
	
	/**
	 * pre select map type button according to map type on URL
	 */
	selectMapTypeButton() {
		const mapTypeParamUrl = window.location.pathname.split('/')[6];
		this.setActiveMapTypeLabel(mapTypeParamUrl);
		this.toggleMapLegend();
	}

	/**
	 * set active label to current mapType and remove it to others
	 */
	setActiveMapTypeLabel(mapType) {
		const mapTypeButtonList = document.querySelectorAll('.btn-map-type');
		for (const button of mapTypeButtonList) {
			const isActive = button.dataset.mapType === mapType;
			button.parentElement.classList.toggle('active', isActive);
			if (isActive) this.mapType = button;
		}
	}
	
	/**
	 * get current geolocation informations (zoom, lattitude an longitude) on map
	 */
	getGeolocationInformations() {
		if (this.mapRef) {
			const center = this.mapRef.getCenter();
			this.latitude = center.lat;
			
			this.zoom = this.mapRef.getZoom();
			let calculatedLongitude = center.lng % 360;
			
			if (calculatedLongitude < -180) {
				calculatedLongitude = calculatedLongitude % 180 + 180;
			} else if (calculatedLongitude > 180) {
				calculatedLongitude = calculatedLongitude % 180 - 180;
			}
			
			this.longitude = calculatedLongitude;
		}
		return {
			zoom: this.zoom,
			latitude: this.latitude,
			longitude: this.longitude
		};
	}
	
	/**
	 * change map type on click on map type button (toggle between signal and download)
	 * @param event
	 */
	toggleMapType(event) {
		this.mapType = event.currentTarget;
		this.updateUrl(true);
		this.toggleMapLegend();
		this.updateHeaderComponent();
		this.updateDescriptionComponent();
		this.setActiveMapTypeLabel(this.mapType.dataset.mapType);
	}
	
	/**
	 * change legend image on map
	 */
	toggleMapLegend() {
		const legendImageList = document.querySelectorAll('.container-legend-img');
		if (this.mapType.dataset) {
			legendImageList.forEach((image) => {
				if (`containerLegend${ FormatString.capitalize(this.mapType.dataset.mapType) }` === image.id) {
					image.style.display = 'flex';
				} else {
					image.style.display = 'none';
				}
			})
		}
	}
	
	/**
	 * Initialize map depending on map type
	 * @param tileType - string (signal | coverage | 5G)
	 */
	async initializeMap(tileType) {
		
		const urlParams = new URLSearchParams(window.location.search);
		const L = await import('leaflet');
		this.tilesMap = {
			arcGis: L.tileLayer(`https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}`, {
				attribution: '&copy; Esri, HERE, Garmin, Intermap, increment P Corp., GEBCO, USGS, FAO, NPS, NRCAN, GeoBase, IGN, Kadaster NL, Ordnance Survey, Esri Japan, METI, Esri China (Hong Kong), &copy; OpenStreetMap contributors, and the GIS User Community',
				noWrap: false
			}),
			openStreetMap: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
				attribution: '<span class=\\"copyright\\">&copy; <a href=\\"https://osm.org/copyright\\">OpenStreetMap</a> contributors</span>',
				noWrap: false
			}),
			hereWeGo: L.tileLayer('https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/{format}?apiKey={apiKey}&lang={language}&style={style}', {
				attribution: 'Map &copy; 1987-2024 <a href="https://developer.here.com" target="_blank">HERE</a>',
				subdomains: '1234',
				apiKey: process.env.HEREWEGO_KEY,
				style: 'explore.satellite.day',
				maxZoom: 20,
				language: 'en',
				format: 'png8'
			})
		}
		
		const mapConfiguration = {
			'minZoom': 3,
			'maxZoom': 16,
			'zoom': urlParams.get('zoom') && urlParams.get('zoom') >= 2 && urlParams.get('zoom') <= 16 ? urlParams.get('zoom') : 2,
			'latitude': urlParams.get('ll') ? urlParams.get('ll') : 20,
			'longitude': urlParams.get('lg') ? urlParams.get('lg') : 0,
			'titleUrl': 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
			'tileAttribution': '<span class=\\"copyright\\">&copy; <a href=\\"https://osm.org/copyright\\">OpenStreetMap</a> contributors</span>'
		};
		
		const mapDefaultOptions = {
			attributionControl: (mapConfiguration.tileAttribution && mapConfiguration.tileAttribution.length),
			scrollWheelZoom: true,
			touchZoom: true,
			minZoom: mapConfiguration.minZoom,
			maxZoom: mapConfiguration.maxZoom
		}
		
		this.mapConfig = mapConfiguration;
		
		this.geoInfo = window.location.pathname.split('/')[4];
		const searchParams = new URLSearchParams(window.location.search);
		if (tileType !== '5G' && (this.geoInfo !== '-' || (searchParams.get('ll') && searchParams.get('lg') && searchParams.get('zoom')))) {
			const cityLatitude = document.querySelector('#map').dataset.cityLatitude;
			const cityLongitude = document.querySelector('#map').dataset.cityLongitude;
			
			let geolocationInformationInit = {
				latitude: this.mapConfig.latitude,
				longitude: this.mapConfig.longitude,
				zoom: this.mapConfig.zoom
			}
			
			if (cityLatitude && cityLongitude) {
				geolocationInformationInit = {
					latitude: cityLatitude,
					longitude: cityLongitude,
					zoom: 12
				}
			} else if (searchParams.get('ll') && searchParams.get('lg') && searchParams.get('zoom')) {
				geolocationInformationInit = {
					latitude: searchParams.get('ll'),
					longitude: searchParams.get('lg'),
					zoom: searchParams.get('zoom')
				}
			}
			
			this.mapRef = L.map('map', mapDefaultOptions
			).setView([
					geolocationInformationInit.latitude,
					geolocationInformationInit.longitude],
				geolocationInformationInit.zoom);
		} else {
			this.mapRef = L.map('map', mapDefaultOptions
			).setView([this.mapConfig.latitude, this.mapConfig.longitude], this.mapConfig.zoom);
		}
		
		this.selectedBackgroundLayer = 'arcGis';
		
		this.tilesMap[this.selectedBackgroundLayer].addTo(this.mapRef);
		
		if (tileType === '5G') {
			const nperfTitle5GLayer = L.tileLayer(`https://${ Environment.getAppDomain() }/nr-map-{z}-{x}-{y}.webp`, {
				noWrap: false
			});
			this.mapRef.addLayer(nperfTitle5GLayer);
			this.selectedLayer = nperfTitle5GLayer;
		} else {
			const ispParamUrl = window.location.pathname.split('/')[5];
			const ispIdParamUrl = ispParamUrl.split('.')[0];
			
			const titleUrlArcgis = `https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}`;
			const titleUrlNperf = `https://${ Environment.getAppDomain() }/${ this.mapType.dataset.mapType }-${ ispIdParamUrl !== '-' ? ispIdParamUrl : '' }-{z}-{x}-{y}.webp${ this.mapVersion ? '?v=' + this.mapVersion : '' }`;
			
			const nperfTitleDefaultLayer = L.tileLayer(ispIdParamUrl !== '-' ? titleUrlNperf : titleUrlArcgis, {
				noWrap: false
			});
			this.mapRef.addLayer(nperfTitleDefaultLayer);
			this.selectedLayer = nperfTitleDefaultLayer;
		}
	}
	
	/**
	 * change tile layer depending on selected layer type on map
	 * @param tileType - string (signal | download | 5G)
	 */
	async resetTileLayer(tileType) {
		let nperfTileLayer;
		const L = await import('leaflet');
		
		if (tileType === '5G') {
			nperfTileLayer = L.tileLayer(`https://${ Environment.getAppDomain() }/nr-map-{z}-{x}-{y}.webp`, {
				noWrap: false
			});
		} else {
			const ispParamUrl = window.location.pathname.split('/')[5];
			const ispIdParamUrl = ispParamUrl.split('.')[0];
			const titleUrlArcgis = `https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}`;
			const titleUrlNperf = `https://${ Environment.getAppDomain() }/${ this.mapType.dataset.mapType }-${ ispIdParamUrl !== '-' ? ispIdParamUrl : '' }-{z}-{x}-{y}.webp${ this.mapVersion ? '?v=' + this.mapVersion : '' }`;
			
			nperfTileLayer = L.tileLayer(ispIdParamUrl !== '-' ? titleUrlNperf : titleUrlArcgis, {
				noWrap: false
			});
		}
		
		if (this.mapRef.hasLayer(this.selectedLayer)) {
			this.mapRef.removeLayer(this.selectedLayer);
			this.selectedLayer = nperfTileLayer;
			this.mapRef.addLayer(nperfTileLayer);
		}
	}
	
	/**
	 * get icon name depending on nominatim type from search list
	 * @param locationType - string
	 * @return icon name
	 */
	getIconNameLocationType(locationType) {
		const iconLocationList = {
			'administrative': 'npicn-region',
			'residential': 'npicn-region',
			'locality': 'npicn-region',
			'city': 'npicn-building',
			'postcode': 'npicn-building',
			'primary': 'npicn-highway',
			'secondary': 'npicn-road',
			'road': 'npicn-road',
			'street': 'npicn-road-tree',
			'track': 'npicn-road-tree',
			
		}
		
		return iconLocationList[locationType];
	}
	
	/**
	 * get nominatim location search list from API
	 * @param query
	 * @param callback
	 * @param mapPage - string (mapIndex | 5G)
	 */
	getResultSearchList(query, callback, mapPage = 'mapIndex') {
		const controlSelectCountry = mapPage === '5G' ? '' : this.selectCountry.tomselect.getValue();
		axios.post(`/${ this.selectedLocale.dataset.languageSelected }/map/map-search-autocomplete`, {
				keywords: query,
				countryCode: controlSelectCountry
			})
			.then((response) => {
				const searchResultList = [];
				const allowTypes = ['locality', 'postcode', 'city', 'residential', 'track', 'road', 'street', 'primary', 'secondary', 'tertiary', 'administrative'];
				
				Object.entries(response.data.data.location.nominatim).forEach((search) => {
					if (allowTypes.includes(search[1].type)) {
						const isResultDuplicate = searchResultList.find((result) => JSON.stringify(result.boundingCoordinates) === JSON.stringify(search[1].boundingbox));
						if (!isResultDuplicate) {
							const postcode = search[1].address.postcode ?? '';
							const state = search[1].address.country_code !== 'fr' ? (search[1].address.state ?? '') : '';
							const additionalInfo = [postcode, state].filter(Boolean).join(' ');
							searchResultList.push({
								placeId: search[1].place_id,
								name: `${search[1].display_name}${additionalInfo ? ', ' + additionalInfo : ''}`,
								iconName: this.getIconNameLocationType(search[1].type),
								boundingCoordinates: search[1].boundingbox
								
							});
						}
						
					}
					
				});
				callback(searchResultList);
			})
			.catch(function (error) {
				if (error.response && error.response.data.errors) {
					callback();
					GlobalError.openErrorModal(error.response.data.errors.message, 'globalErrorMap');
				}
			});
	}
	
	/**
	 * update location on map and current url
	 * @param mapPage - string (mapIndex | 5G)
	 */
	refreshMap(mapPage = 'mapIndex') {
		const controlInputMapSearch = this.inputMapSearch.tomselect;
		const selectedLocation = controlInputMapSearch.getValue();
		if (selectedLocation) {
			const locationCoordinates = controlInputMapSearch.getItem(selectedLocation).dataset.boundingBox.split(',');
			this.mapRef.fitBounds([[locationCoordinates[0], locationCoordinates[2]], [locationCoordinates[1], locationCoordinates[3]]]);
			if (mapPage !== '5G') {
				this.mapRef.on('zoomend', () => {
					this.updateUrl(true);
				});
			}
		}
	}
	
	/**
	 * change tile layer on select on tile button
	 * @param selectedTile - string (openStreetMap | hereWeGo | arcGis)
	 * @param tileType - string (signal | download | 5G)
	 */
	toggleTileMap(selectedTile, tileType) {
		if (this.selectedBackgroundLayer !== selectedTile) {
			this.mapRef.removeLayer(this.tilesMap[this.selectedBackgroundLayer]);
			this.mapRef.addLayer(this.tilesMap[selectedTile]);
			this.selectedBackgroundLayer = selectedTile;
			this.resetTileLayer(tileType).then();
		}
	}
	
	/**
	 * get last time date of map data was updated from API
	 */
	getLastUpdate() {
		const lat0 = this.mapRef.getBounds().getNorthEast().lat;
		const lng1 = this.mapRef.getBounds().getNorthEast().lng;
		const lat1 = this.mapRef.getBounds().getSouthWest().lat;
		const lng0 = this.mapRef.getBounds().getSouthWest().lng;
		const zoom = this.mapRef.getZoom();
		
		axios.post(`/${ this.selectedLocale.dataset.languageSelected }/map/get-count-signal`, {
				swLng: lng0,
				neLat: lat0,
				neLng: lng1,
				swLat: lat1,
				zoom: zoom,
				mapType: this.mapType.dataset.mapType,
				ispId: `${ this.selectIsp.tomselect.getValue() }`,
				v: this.mapVersion ? this.mapVersion : ''
				
			})
			.then((response) => {
				const lastDateElement = document.querySelector('#lastUpdate');
				const lastDateContainer = document.querySelector('.container-last-update');
				
				if (response.data.LastUpdate) {
					lastDateContainer.style.display = 'block';
					lastDateElement.textContent = `${ response.data.LastUpdate } UTC`;
				} else {
					lastDateContainer.style.display = 'none';
				}
				
			})
			.catch(function (error) {
				if (error.response && error.response.data.errors) {
					GlobalError.openErrorModal(error.response.data.errors.message, 'globalErrorMap');
				}
			});
	}
	
	/**
	 * update map type and country on header component
	 */
	updateHeaderComponent() {
		
		axios.post(`/${ this.selectedLocale.dataset.languageSelected }/map/update-map-header-component`, {
				countryCode: this.selectCountry.tomselect.getValue(),
				locale: this.selectedLocale.dataset.languageSelected,
				mapType: this.mapType.dataset.mapType,
				geoInfo: this.getUrl()['geoInfo'],
				ispInfo: this.getUrl()['ispInfo'],
				selectedCountry: this.getUrl()['geoInfo'].split('.').pop(),
				selectedIspId: this.getUrl()['ispInfo'].split('.')[0] !== '-' ? this.getUrl()['ispInfo'].split('.')[0] : null
				
			})
			.then((response) => {
				const headerElement = document.querySelector('.container-map-header');
				headerElement.innerHTML = response.data;
			})
			.catch(function (error) {
				if (error.response && error.response.data.errors) {
					GlobalError.openErrorModal(error.response.data.errors.message, 'globalErrorMap');
				}
			});
	}
	
	/**
	 * update top cities and isp list on description component
	 */
	updateDescriptionComponent() {
		axios.post(`/${ this.selectedLocale.dataset.languageSelected }/map/update-map-description-component`, {
				countryCode: this.selectCountry.tomselect.getValue() ? this.selectCountry.tomselect.getValue() : 'ww',
				locale: this.selectedLocale.dataset.languageSelected,
				mapType: this.mapType.dataset.mapType,
				geoInfo: this.getUrl()['geoInfo'],
				selectedCountry: this.getUrl()['geoInfo'].split('.').pop(),
				selectedIspId: this.selectIsp.tomselect.getValue(),
				ispInfo: window.location.pathname.split('/')[5]
			})
			.then((response) => {
				const headerElement = document.querySelector('.container-description');
				headerElement.innerHTML = response.data;
			})
			.catch(function (error) {
				if (error.response && error.response.data.errors) {
					GlobalError.openErrorModal(error.response.data.errors.message, 'globalErrorMap');
				}
			});
	}
	
	/**
	 * display fullscreen view
	 */
	displayFullScreenElements(containerMap, isFullscreen = true) {
		const containerListElement = document.querySelector('.container-select-map .container-lists');
		const containerMapTypeElement = document.querySelector('.container-select-map .container-map-type');
		const buttonFullscreenIconList = document.querySelectorAll('.btn-full-screen i');
		
		if (isFullscreen) {
			containerMap.classList.add('container-map-full-screen');
			if (containerListElement && containerMapTypeElement) {
				
				containerListElement.classList.replace('col-lg-3', 'col-lg-4');
				
				containerMapTypeElement.classList.replace('col-lg-6', 'col-lg-5');
			}
			
			buttonFullscreenIconList.forEach((buttonFullscreenIcon) => {
				buttonFullscreenIcon.classList.replace('npicn-full-screen', 'npicn-windows-view');
			});
			
		} else {
			containerMap.classList.remove('container-map-full-screen');
			if (containerListElement && containerMapTypeElement) {
				
				containerListElement.classList.replace('col-lg-4', 'col-lg-3');
				
				containerMapTypeElement.classList.replace('col-lg-5', 'col-lg-6');
			}
			buttonFullscreenIconList.forEach((buttonFullscreenIcon) => {
				buttonFullscreenIcon.classList.replace('npicn-windows-view', 'npicn-full-screen');
			})
		}
	}
	
	/**
	 * exit from fullscreen view
	 */
	exitFullscreen(containerMap) {
		if (document.fullscreenElement) {
			document.exitFullscreen().then(() => {
				this.displayFullScreenElements(containerMap, false);
			});
		} else if (document.webkitFullscreenElement) {
			document.webkitExitFullscreen();
			this.displayFullScreenElements(containerMap, false);
		} else if (document.mozFullScreenElement) {
			document.mozCancelFullScreen();
			this.displayFullScreenElements(containerMap, false);
		} else if (document.msFullscreenElement) {
			document.msExitFullscreen();
			this.displayFullScreenElements(containerMap, false);
		}
	}
	
	/**
	 * enable map on fullscreen mode
	 */
	enableFullscreen(containerMap) {
		if (containerMap.requestFullscreen) {
			containerMap.requestFullscreen().then(() => {
				this.displayFullScreenElements(containerMap);
			}).catch((error => GlobalError.openErrorModal(error.response.data.errors.message, 'globalErrorMap')));
		} else if (containerMap.webkitRequestFullscreen) {
			containerMap.webkitRequestFullscreen();
			this.displayFullScreenElements(containerMap);
		} else if (containerMap.mozRequestFullScreen) {
			containerMap.mozRequestFullScreen();
			this.displayFullScreenElements(containerMap);
		} else if (containerMap.msRequestFullscreen) {
			containerMap.msRequestFullscreen();
			this.displayFullScreenElements(containerMap);
		}
	}
	
	/**
	 * enable/disable fullscreen mode on click on fullscreen button
	 */
	toggleFullscreen() {
		const containerMapElement = document.querySelector('.container-select-map');
		if (containerMapElement) {
			if (this.isFullscreen) {
				this.exitFullscreen(containerMapElement);
			} else {
				this.enableFullscreen(containerMapElement);
			}
		}
		this.isFullscreen = !this.isFullscreen;
	}
	
	/**
	 * enable/disable isp message overlay on map
	 */
	toggleOverlay() {
		const overlayElement = document.querySelector('#overlayEnableMap');
		const containerSearchElement = document.querySelector('.container-map-search');
		const containerMapType = document.querySelector('.container-map-type');
		const containerInputGroupAppend = document.querySelector('.input-group-append');
		if (this.selectIsp.tomselect.getValue()) {
			overlayElement.classList.add('d-none');
			containerSearchElement.classList.remove('d-none');
			containerSearchElement.classList.add('active');
			containerInputGroupAppend.style.opacity = 1;
			
			this.inputMapSearch.tomselect.enable();
			if (containerMapType) {
				containerMapType.classList.remove('enable-search-bar');
			}
		} else {
			overlayElement.classList.remove('d-none');
			containerSearchElement.classList.remove('d-none');
			containerSearchElement.classList.add('d-block');
			this.inputMapSearch.tomselect.disable();
			containerInputGroupAppend.style.opacity = 0.5;
			containerSearchElement.classList.remove('active');
			if (containerMapType) {
				containerMapType.classList.add('enable-search-bar');
			}
		}
	}
	
	/**
	 * clear search list
	 */
	refreshSearchOptions() {
		if (this.inputMapSearch.tomselect.getValue().length === 0) {
			this.inputMapSearch.tomselect.clearOptions();
		}
		
	}
}